import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { api, USER_ID } from "../api";
import { toast } from "sonner";
import { badgeToast, melaToast, MelaCoin } from "./BadgesSheet";

const W = 460, H = 340, GROUND = H - 26;
const POTS = [
  { x: 235, r: 20, label: "1x" },
  { x: 330, r: 16, label: "2x" },
  { x: 415, r: 13, label: "3x" },
];
const HOWTO_KEY = "xp_howto_ringtoss";

export const RingToss = ({ refresh }) => {
  const canvasRef = useRef(null);
  const g = useRef(null);
  const rafRef = useRef(0);
  const [howto, setHowto] = useState(() => localStorage.getItem(HOWTO_KEY) !== "1");
  const [phase, setPhase] = useState("idle"); // idle | playing | done
  const [ringsLeft, setRingsLeft] = useState(8);
  const [landed, setLanded] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const drawScene = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const st = g.current || {};
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#140E06";
    ctx.fillRect(0, 0, W, H);
    // stall canopy stripes
    for (let i = 0; i < 12; i++) {
      ctx.fillStyle = i % 2 ? "#E25822" : "#D4AF37";
      ctx.beginPath();
      ctx.moveTo(i * 40, 0); ctx.lineTo(i * 40 + 40, 0); ctx.lineTo(i * 40 + 20, 16); ctx.fill();
    }
    // ground
    ctx.fillStyle = "#241708";
    ctx.fillRect(0, GROUND, W, H - GROUND);
    ctx.strokeStyle = "#8a6d1f";
    ctx.beginPath(); ctx.moveTo(0, GROUND); ctx.lineTo(W, GROUND); ctx.stroke();
    // clay pots + pegs
    POTS.forEach((p) => {
      ctx.fillStyle = "#8a4a1f";
      ctx.beginPath(); ctx.ellipse(p.x, GROUND - 10, p.r + 6, 12, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#a35a24";
      ctx.beginPath(); ctx.ellipse(p.x, GROUND - 22, p.r, 9, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "#D4AF37";
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(p.x, GROUND - 22); ctx.lineTo(p.x, GROUND - 58); ctx.stroke();
      ctx.lineWidth = 1;
      ctx.fillStyle = "#F0C742";
      ctx.font = "900 10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(p.label, p.x, GROUND + 14);
    });
    // launch spot
    ctx.fillStyle = "#D4AF37";
    ctx.beginPath(); ctx.arc(46, GROUND - 30, 4, 0, Math.PI * 2); ctx.fill();
    // aiming line
    if (st.aiming && st.drag) {
      ctx.strokeStyle = "rgba(240,199,66,0.6)";
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(46, GROUND - 30);
      ctx.lineTo(46 + st.drag.dx * -2.2, GROUND - 30 + st.drag.dy * -2.2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // flying / resting rings
    (st.rings || []).forEach((r) => {
      ctx.strokeStyle = r.hit ? "#F0C742" : "#c9b598";
      ctx.lineWidth = 4;
      ctx.beginPath(); ctx.ellipse(r.x, r.y, 11, r.flying ? 7 : 5, 0, 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 1;
    });
  }, []);

  const step = useCallback(() => {
    const st = g.current;
    if (!st) return;
    let anyFlying = false;
    st.rings.forEach((r) => {
      if (!r.flying) return;
      anyFlying = true;
      r.x += r.vx;
      r.vy += 0.5;
      r.y += r.vy;
      if (r.y >= GROUND - 26 && r.vy > 0) {
        r.flying = false;
        r.y = GROUND - 26;
        const pot = POTS.find((p) => Math.abs(r.x - p.x) <= p.r * 0.75);
        if (pot) {
          r.hit = true;
          r.x = pot.x;
          st.landed += 1;
          setLanded(st.landed);
        }
      }
      if (r.x > W + 30) r.flying = false;
    });
    drawScene();
    if (anyFlying) rafRef.current = requestAnimationFrame(step);
    else if (st.thrown >= 8) finish();
  }, [drawScene]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/arcade/session", { user_id: USER_ID, game: "ringtoss" });
      g.current = { session: r.data.session_id, rings: [], thrown: 0, landed: 0, aiming: false, drag: null };
      setRingsLeft(8);
      setLanded(0);
      setPhase("playing");
      setTimeout(drawScene, 50);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    setBusy(false);
  };

  const finish = async () => {
    const st = g.current;
    if (!st || st.finished) return;
    st.finished = true;
    setPhase("done");
    try {
      const r = await api.post("/arcade/ringtoss/submit", { user_id: USER_ID, session_id: st.session, rings: st.landed });
      setResult(r.data);
      if (r.data.mela_result?.awarded) melaToast(r.data.mela_result.message);
      badgeToast(r.data.new_badges);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the round"); }
  };

  const ptr = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * W, y: ((e.clientY - rect.top) / rect.height) * H };
  };

  const onDown = (e) => {
    const st = g.current;
    if (!st || st.thrown >= 8 || st.rings.some((r) => r.flying)) return;
    e.preventDefault();
    st.aiming = true;
    st.start = ptr(e);
    st.drag = { dx: 0, dy: 0 };
  };
  const onMove = (e) => {
    const st = g.current;
    if (!st?.aiming) return;
    const p = ptr(e);
    st.drag = { dx: p.x - st.start.x, dy: p.y - st.start.y };
    drawScene();
  };
  const onUp = () => {
    const st = g.current;
    if (!st?.aiming) return;
    st.aiming = false;
    const { dx, dy } = st.drag || { dx: 0, dy: 0 };
    const power = Math.min(26, Math.hypot(dx, dy) / 6);
    if (power < 2) { st.drag = null; drawScene(); return; }
    st.rings.push({ x: 46, y: GROUND - 30, vx: Math.max(2, -dx / 6), vy: Math.min(-3, dy / 6) - power * 0.35, flying: true, hit: false });
    st.thrown += 1;
    setRingsLeft(8 - st.thrown);
    st.drag = null;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
  };

  return (
    <div data-testid="game-ring-toss" className="rounded-2xl bg-[#121212] border border-[#D4AF37]/30 p-5">
      <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">CHOORI DA CHALLA · ਚੂੜੀ ਦਾ ਛੱਲਾ</p>
      <p className="text-xs text-white/50 mt-1">Toss 8 wooden rings onto the clay-pot pegs. The far pegs are the pride of the pind.</p>

      {howto && (
        <div data-testid="ringtoss-howto" className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#1A1208] p-4">
          <p className="text-[9px] tracking-[0.25em] text-[#D4AF37] font-black">HOW TO PLAY</p>
          <p className="text-xs text-white/70 mt-1.5 leading-relaxed">Press on the stall, pull back and down like a slingshot, then release to toss the ring. Pull further for more power. Land rings on the golden pegs — 8 rings per round.</p>
          <button data-testid="ringtoss-howto-ok" onClick={() => { localStorage.setItem(HOWTO_KEY, "1"); setHowto(false); }}
            className="mt-3 h-9 px-5 rounded-full bg-[#D4AF37] text-black text-[10px] font-black tracking-widest">GOT IT</button>
        </div>
      )}

      {!howto && phase === "idle" && (
        <button data-testid="ringtoss-start" onClick={start} disabled={busy}
          className="mt-4 w-full h-12 rounded-full bg-[#D4AF37] text-black font-black text-sm tracking-widest disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : "START THE ROUND"}
        </button>
      )}

      {phase !== "idle" && (
        <>
          <div className="flex items-center justify-between mt-4 text-xs font-black">
            <span data-testid="ringtoss-left" className="text-white/60">RINGS LEFT: {ringsLeft}</span>
            <span data-testid="ringtoss-landed" className="text-[#F0C742]">LANDED: {landed}</span>
          </div>
          <div className="mt-2 rounded-xl overflow-hidden border border-white/10 select-none max-w-[540px] mx-auto">
            <canvas ref={(cv) => { canvasRef.current = cv; if (cv && phase === "playing") drawScene(); }}
              width={W} height={H} data-testid="ringtoss-canvas" className="w-full block touch-none cursor-crosshair"
              onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} />
          </div>
        </>
      )}

      {phase === "done" && result && (
        <div data-testid="ringtoss-result" className="mt-3 rounded-xl border border-[#D4AF37]/40 bg-[#1C160A] p-4 text-center">
          <p className="font-display text-2xl font-bold">{result.rings}/8 rings landed</p>
          {result.mela_result && <p className="text-xs text-[#F0C742] font-bold mt-1 flex items-center justify-center gap-1.5"><MelaCoin size={15} /> {result.mela_result.message}</p>}
          <button data-testid="ringtoss-again" onClick={start} className="mt-3 h-10 px-6 rounded-full border border-[#D4AF37]/60 text-[#D4AF37] text-[10px] font-black tracking-widest">TOSS AGAIN</button>
        </div>
      )}
    </div>
  );
};
